import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { HiOutlineMail, HiOutlineX, HiOutlineCheck } from 'react-icons/hi';
import { sendWeeklyReportReminder } from '../../../../store/slices/performanceReviewSlice';
import { format, startOfWeek, endOfWeek } from 'date-fns';
import { sendWeeklyReportReminders } from '../../../../emailjs/emailService';
import './WeeklyReminderPopup.scss';

const WeeklyReminderPopup = ({ employees }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sendStatus, setSendStatus] = useState({ success: false, count: 0 });
  const dispatch = useDispatch();
  
  const { reminderLoading, reminderSuccess } = useSelector(state => state.performanceReview);
  
  const currentWeekStart = startOfWeek(new Date(), { weekStartsOn: 1 });
  const currentWeekEnd = endOfWeek(currentWeekStart, { weekStartsOn: 1 });
  
  const weekData = {
    startDate: format(currentWeekStart, 'MMM d'),
    endDate: format(currentWeekEnd, 'MMM d, yyyy'),
  };
  
  const togglePopup = () => {
    setIsOpen(!isOpen);
    if (!isOpen) {
      setSelectedEmployees([]);
      setSendStatus({ success: false, count: 0 });
    }
  };
  
  const handleEmployeeSelect = (email) => {
    if (selectedEmployees.includes(email)) {
      setSelectedEmployees(selectedEmployees.filter(emp => emp !== email));
    } else {
      setSelectedEmployees([...selectedEmployees, email]);
    }
  };
  
  const handleSelectAll = () => {
    if (selectedEmployees.length === employees.length) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(employees.map(emp => emp.email));
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (selectedEmployees.length === 0) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Filter the employees array to only include selected employees
      const selectedEmployeeObjects = employees.filter(emp => 
        selectedEmployees.includes(emp.email)
      );
      
      // First, create blank reports in the database via Redux action
      await dispatch(sendWeeklyReportReminder(selectedEmployeeObjects)).unwrap();
      
      // Then, send beautiful email notifications using our email service
      const emailResult = await sendWeeklyReportReminders(selectedEmployeeObjects, weekData);
      
      // Set status for UI feedback
      setSendStatus({
        success: emailResult.success,
        count: emailResult.sentCount || 0
      });
      
      // Close the popup after successful submission with a slight delay
      if (emailResult.success) {
        setTimeout(() => {
          togglePopup();
          setIsSubmitting(false);
        }, 1500);
      } else {
        setIsSubmitting(false);
      }
      
    } catch (error) {
      console.error('Error sending reminders:', error);
      setIsSubmitting(false);
    }
  };
  
  return (
    <>
      <button 
        className="action-button"
        onClick={togglePopup}
        disabled={reminderLoading}
      >
        <HiOutlineMail className="icon" />
        {reminderLoading ? 'Sending...' : 'Send Weekly Reminders'}
      </button>
      
      {isOpen && (
        <div className="reminder-popup-overlay">
          <div className="reminder-popup">
            <div className="popup-header">
              <h3>Send Weekly Performance Report Reminders</h3>
              <button className="close-button" onClick={togglePopup}>
                <HiOutlineX />
              </button>
            </div>
            
            <div className="popup-body">
              <div className="info-section">
                <p>Select employees to send Weekly Performance Report Reminders for:</p>
                <p className="date-range">
                  <strong>Week of {format(currentWeekStart, 'MMM d')} - {format(currentWeekEnd, 'MMM d, yyyy')}</strong>
                </p>
                <p className="info-note">
                  This will create a blank report for each selected employee and send them a notification.
                </p>
              </div>
              
              <div className="select-all-row">
                <label className="checkbox-container">
                  <input 
                    type="checkbox"
                    checked={selectedEmployees.length === employees.length}
                    onChange={handleSelectAll}
                  />
                  <span className="checkmark"></span>
                  <span className="select-all-text">Select All Employees</span>
                </label>
                <span className="count-badge">
                  {selectedEmployees.length} selected
                </span>
              </div>
              
              <div className="employee-list">
                {employees.map(employee => (
                  <div key={employee.email} className="employee-item">
                    <label className="checkbox-container">
                      <input 
                        type="checkbox"
                        checked={selectedEmployees.includes(employee.email)}
                        onChange={() => handleEmployeeSelect(employee.email)}
                      />
                      <span className="checkmark"></span>
                      <div className="employee-info">
                        <span className="employee-name">{employee.name}</span>
                        <span className="employee-email">{employee.email}</span>
                      </div>
                    </label>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="popup-footer">
              <button 
                type="button" 
                className="cancel-button" 
                onClick={togglePopup}
                disabled={isSubmitting}
              >
                Cancel
              </button>
              <button 
                type="button" 
                className="submit-button" 
                onClick={handleSubmit}
                disabled={selectedEmployees.length === 0 || isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <span className="loading-spinner"></span>
                    <span>Sending...</span>
                  </>
                ) : sendStatus.success ? (
                  <>
                    <HiOutlineCheck className="icon" />
                    <span>Sent Successfully ({sendStatus.count})</span>
                  </>
                ) : (
                  <>
                    <HiOutlineMail className="icon" />
                    <span>Send Reminders</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WeeklyReminderPopup; 